import { Download, Loader2, Trophy } from "lucide-react";
import * as XLSX from "xlsx";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { fmtDateTime } from "@/lib/datetime";

const MEDAL = ["#FFD700", "#C0C0C0", "#CD7F32"];

const downloadExcel = (data, t) => {
  const header = [t("rank"), t("player"), t("contact_your_name"), t("contact_store")];
  const body = data.players.filter((p) => p.contact).map((p) => [p.rank, p.name, p.contact.name, p.contact.store]);
  const ws = XLSX.utils.aoa_to_sheet([header, ...body]);
  ws["!cols"] = [{ wch: 6 }, { wch: 20 }, { wch: 24 }, { wch: 28 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Winners");
  XLSX.writeFile(wb, `winners_${data.pin}.xlsx`);
};

export const GameResultsDialog = ({ open, onOpenChange, data, loading, t, lang }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-lg" data-testid="game-results-dialog">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2"><Trophy className="w-5 h-5 text-[#FFD700]" />{t("game_results")}</DialogTitle>
        <DialogDescription>{data ? `${data.title} · ${data.pin} · ${fmtDateTime(data.created_at, lang)}` : t("loading")}</DialogDescription>
      </DialogHeader>
      {loading || !data ? (
        <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
      ) : (
        <>
          {data.players.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground text-sm">{t("no_scores")}</div>
          ) : (
            <div className="max-h-[60vh] overflow-y-auto rounded-lg border border-border" data-testid="game-results-list">
              <table className="w-full text-sm">
                <thead className="text-muted-foreground text-left sticky top-0 bg-card">
                  <tr className="border-b border-border">
                    <th className="px-3 py-2 font-semibold w-12">{t("rank")}</th>
                    <th className="px-3 py-2 font-semibold">{t("player")}</th>
                    <th className="px-3 py-2 font-semibold text-center">{t("correct_answers")}</th>
                    <th className="px-3 py-2 font-semibold text-right">{t("score")}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.players.map((p) => (
                    <tr key={p.rank} data-testid={`game-result-row-${p.rank}`} className="border-b border-border/50 last:border-0 hover:bg-secondary/40">
                      <td className="px-3 py-2">
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold"
                          style={p.rank <= 3 ? { background: MEDAL[p.rank - 1], color: "#1a1420" } : { color: "var(--muted-foreground)" }}>{p.rank}</span>
                      </td>
                      <td className="px-3 py-2 font-semibold">{p.name}</td>
                      <td className="px-3 py-2 text-center text-muted-foreground">{p.correct}/{data.total_questions}</td>
                      <td className="px-3 py-2 text-right font-bold tabular-nums">{p.score}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {data.players.some((p) => p.contact) && (
            <div className="mt-4" data-testid="winner-contacts-section">
              <div className="text-xs uppercase tracking-wider font-bold text-muted-foreground mb-2 flex items-center gap-1.5">
                <Trophy className="w-3.5 h-3.5 text-[#FFD700]" />{t("winner_contacts")}
                <Button size="sm" variant="secondary" className="ml-auto h-7" data-testid="download-winners-excel" onClick={() => downloadExcel(data, t)}>
                  <Download className="w-3.5 h-3.5 mr-1.5" />{t("download_excel")}
                </Button>
              </div>
              <div className="rounded-lg border border-border divide-y divide-border/50">
                {data.players.filter((p) => p.contact).map((p) => (
                  <div key={p.rank} data-testid={`winner-contact-${p.rank}`} className="px-3 py-2 flex items-center justify-between gap-3 text-sm">
                    <div className="min-w-0">
                      <span className="font-semibold">{p.contact.name}</span>
                      <span className="text-muted-foreground"> · {p.contact.store}</span>
                    </div>
                    <span className="text-xs text-muted-foreground shrink-0">#{p.rank} · {p.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </DialogContent>
  </Dialog>
);
